import { throttle } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    MIN_UPDATE_INTERVAL_MS,
    previewShownRoiIds,
} from '~/containers/viewer/viewport/previewRoiIds';
import {
    ContourRedoRequestedEvent,
    ContourUndoRequestedEvent,
    domainEventBus,
    RoiContourEditEvent,
    RoiContoursModifiedEvent,
    RoiPropertiesUpdatedEvent,
} from '~/domain/events';
import type { Environment } from '~/engine/data/Environment';
import { previewSurfaceColor } from '~/engine/tool/actions/contouring-actions/previewSurfaceColor';
import {
    PreviewVolume,
    previewVolumeOf,
} from '~/engine/tool/actions/contouring-actions/RoiPreviewRenderer';
import { sameElements } from '~/utils/array';
import type { RoiId } from '~/domain/changes/ids';

export interface CommittedRoiVolumes {
    /** Ready-to-upload sub-volumes of the shown ROIs, in the set's order. */
    volumes: readonly PreviewVolume[];
    /** How many of the selection have something to draw. */
    shownCount: number;
    /** How many of the selection could be drawn at all. */
    drawableCount: number;
    /**
     * Zero until the volumes of a selection have been resolved, then bumped
     * when the selection moved, as opposed to an edit landing on what is shown.
     */
    selectionToken: number;
}

/**
 * The committed structures the 3-D preview draws, as GPU-ready volumes: of
 * `drawableRoiIds`, the ROIs of `environment` that have contours, within
 * the preview's slice budget. Kept current, throttled, as those ROIs are
 * edited or recoloured in the structure set `structureSetUid`; with no set
 * named no edit refreshes them.
 */
export function useCommittedRoiVolumes(
    environment: Environment | undefined,
    structureSetUid: string | undefined,
    drawableRoiIds: readonly RoiId[]
): CommittedRoiVolumes {
    const [shownRoiIds, setShownRoiIds] = useState<readonly RoiId[]>([]);
    const [volumes, setVolumes] = useState<readonly PreviewVolume[]>([]);
    const [selectionToken, setSelectionToken] = useState(0);
    const drawableRoiIdsRef = useRef(drawableRoiIds);
    const environmentRef = useRef(environment);
    const structureSetUidRef = useRef(structureSetUid);
    const shownRoiIdsRef = useRef<readonly RoiId[]>(shownRoiIds);
    // Keyed on what could be drawn, so an ROI with no contours yet keeps the subscription that shows its first stroke.
    const isActive = drawableRoiIds.length > 0;
    useEffect(() => {
        drawableRoiIdsRef.current = drawableRoiIds;
        environmentRef.current = environment;
        structureSetUidRef.current = structureSetUid;
    });
    const resolveShown = useCallback((): readonly RoiId[] => {
        const environment = environmentRef.current;
        const next = previewShownRoiIds(drawableRoiIdsRef.current, (roiId) => {
            const texture = environment?.getRoi(roiId)?.texture;
            if (texture === undefined || texture.isEmpty) {
                return null;
            }
            return Math.round(texture.sizePx[2]);
        });
        const previous = shownRoiIdsRef.current;
        if (sameElements(previous, next)) {
            return previous;
        }
        shownRoiIdsRef.current = next;
        setShownRoiIds(next);
        return next;
    }, []);
    const rebuild = useCallback((): void => {
        const environment = environmentRef.current;
        if (!environment) {
            setVolumes([]);
            return;
        }
        const roiIds = resolveShown();
        try {
            const next: PreviewVolume[] = [];
            for (const roiId of roiIds) {
                const roi = environment.getRoi(roiId);
                const texture = roi?.texture;
                if (!roi || !texture || texture.isEmpty) {
                    continue;
                }
                next.push(
                    previewVolumeOf(texture, previewSurfaceColor(roi.color))
                );
            }
            setVolumes(next);
        } catch (error) {
            // Degrades to an empty preview rather than throwing out of an effect or a timer.
            console.warn('RoiPreviewPane: loading the preview failed', error);
            setVolumes([]);
        }
    }, [resolveShown]);
    const throttledRefresh = useMemo(
        () => throttle(rebuild, MIN_UPDATE_INTERVAL_MS),
        [rebuild]
    );
    useEffect(() => {
        if (!isActive) {
            shownRoiIdsRef.current = [];
            setShownRoiIds([]);
            setVolumes([]);
            return;
        }
        rebuild();
        setSelectionToken((token) => token + 1);
    }, [isActive, drawableRoiIds, rebuild]);
    useEffect(() => {
        if (!isActive) {
            return;
        }
        const refreshIfShown = (
            event: RoiContourEditEvent | RoiPropertiesUpdatedEvent
        ) => {
            if (
                !drawableRoiIdsRef.current.includes(event.roiId) ||
                (event instanceof RoiPropertiesUpdatedEvent &&
                    event.updatedProperties.color === undefined)
            ) {
                return;
            }
            // Only an edit or undo/redo of the named set refreshes; with no
            // set named, none does.
            const structureSetUid = structureSetUidRef.current;
            if (
                structureSetUid === undefined ||
                event.structureSetUid !== structureSetUid
            ) {
                return;
            }
            throttledRefresh();
        };
        const subscriptions = [
            RoiContoursModifiedEvent.EVENT_TYPE,
            ContourUndoRequestedEvent.EVENT_TYPE,
            ContourRedoRequestedEvent.EVENT_TYPE,
            RoiPropertiesUpdatedEvent.EVENT_TYPE,
        ].map((eventType) =>
            domainEventBus.subscribe(eventType, refreshIfShown)
        );
        return () => {
            subscriptions.forEach((subscription) => subscription.unsubscribe());
            throttledRefresh.cancel();
        };
    }, [isActive, throttledRefresh]);
    return useMemo(
        () => ({
            volumes,
            shownCount: shownRoiIds.length,
            drawableCount: drawableRoiIds.length,
            selectionToken,
        }),
        [volumes, shownRoiIds, drawableRoiIds, selectionToken]
    );
}
